import axios, { AxiosError, InternalAxiosRequestConfig } from "axios";
import type { FieldSchema } from "@onecreator/shared";

const API_URL = import.meta.env.VITE_API_URL || "/api/v1";

export const api = axios.create({
  baseURL: API_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor - add auth token
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// Response interceptor - handle errors
api.interceptors.response.use(
  (response) => response,
  (
    error: AxiosError<{
      error?: { code?: string; message?: string; details?: unknown };
    }>,
  ) => {
    // Handle 401 - redirect to login
    if (error.response?.status === 401) {
      localStorage.removeItem("token");
      window.location.href = "/login";
    }

    // Preserva la risposta completa per gestire dettagli di validazione
    // Il componente che gestisce l'errore può accedere a error.response.data
    return Promise.reject(error);
  },
);

// Types
export interface ApiResponse<T> {
  success: boolean;
  data: T;
}

export interface PaginatedResponse<T> {
  items: T[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
  };
}

// Auth API
export const authApi = {
  login: (email: string, password: string) =>
    api.post<ApiResponse<{ token: string; user: User }>>("/auth/login", {
      email,
      password,
    }),

  logout: () => api.post<ApiResponse<{ message: string }>>("/auth/logout"),

  me: () => api.get<ApiResponse<{ user: User }>>("/auth/me"),

  changePassword: (currentPassword: string, newPassword: string) =>
    api.post<ApiResponse<{ message: string }>>("/auth/change-password", {
      currentPassword,
      newPassword,
    }),
};

// Users API
export const usersApi = {
  list: (params?: {
    active?: boolean;
    role?: string;
    limit?: number;
    offset?: number;
  }) =>
    api.get<ApiResponse<{ users: User[]; pagination: Pagination }>>("/users", {
      params,
    }),

  get: (id: string) => api.get<ApiResponse<{ user: User }>>(`/users/${id}`),

  create: (data: CreateUserData) =>
    api.post<ApiResponse<{ user: User }>>("/users", data),

  update: (id: string, data: Partial<UpdateUserData>) =>
    api.patch<ApiResponse<{ user: User }>>(`/users/${id}`, data),

  delete: (id: string) =>
    api.delete<ApiResponse<{ message: string }>>(`/users/${id}`),
};

// Clients API
export const clientsApi = {
  list: (params?: {
    active?: boolean;
    q?: string;
    limit?: number;
    offset?: number;
  }) =>
    api.get<ApiResponse<{ clients: Client[]; pagination: Pagination }>>(
      "/clients",
      { params },
    ),

  get: (id: string) =>
    api.get<ApiResponse<{ client: Client }>>(`/clients/${id}`),

  create: (data: CreateClientData) =>
    api.post<ApiResponse<{ client: Client }>>("/clients", data),

  update: (id: string, data: Partial<UpdateClientData>) =>
    api.patch<ApiResponse<{ client: Client }>>(`/clients/${id}`, data),

  delete: (id: string) =>
    api.delete<ApiResponse<{ message: string }>>(`/clients/${id}`),

  getCatalog: (id: string) =>
    api.get<
      ApiResponse<{
        catalog: MergedCatalog;
        restrictions: CatalogRestrictions | null;
      }>
    >(`/clients/${id}/catalog`),

  updateCatalog: (id: string, restrictions: CatalogRestrictions) =>
    api.put<ApiResponse<{ client: Client }>>(
      `/clients/${id}/catalog`,
      restrictions,
    ),
};

// Projects API
export const projectsApi = {
  list: (params?: {
    clientId?: string;
    status?: string;
    q?: string;
    limit?: number;
    offset?: number;
  }) =>
    api.get<ApiResponse<{ projects: Project[]; pagination: Pagination }>>(
      "/projects",
      { params },
    ),

  get: (id: string) =>
    api.get<ApiResponse<{ project: Project }>>(`/projects/${id}`),

  create: (data: CreateProjectData) =>
    api.post<ApiResponse<{ project: Project }>>("/projects", data),

  update: (id: string, data: Partial<UpdateProjectData>) =>
    api.patch<ApiResponse<{ project: Project }>>(`/projects/${id}`, data),

  delete: (id: string) =>
    api.delete<ApiResponse<{ message: string }>>(`/projects/${id}`),

  validate: (id: string) =>
    api.post<ApiResponse<{ valid: boolean; errors: ValidationError[] }>>(
      `/projects/${id}/validate`,
    ),

  export: (id: string) =>
    api.post<ApiResponse<{ config: object; exportedAt: string }>>(
      `/projects/${id}/export`,
    ),

  downloadExport: (id: string) =>
    api.get(`/projects/${id}/export`, { responseType: "blob" }),

  generatePackage: (id: string, deployConfig?: Record<string, unknown>) =>
    api.post(
      `/projects/${id}/package`,
      { deployConfig },
      { responseType: "blob", timeout: 120_000 },
    ),

  // History
  getHistory: (id: string, params?: { limit?: number; offset?: number }) =>
    api.get<ApiResponse<{ history: HistoryEntry[]; pagination: Pagination }>>(
      `/projects/${id}/history`,
      { params },
    ),

  getHistoryVersion: (projectId: string, version: number) =>
    api.get<ApiResponse<{ history: HistoryEntry }>>(
      `/projects/${projectId}/history/${version}`,
    ),

  restoreVersion: (projectId: string, version: number) =>
    api.post<ApiResponse<{ project: Project }>>(
      `/projects/${projectId}/history/${version}/restore`,
    ),

  deleteHistoryVersion: (projectId: string, version: number) =>
    api.delete<ApiResponse<{ message: string }>>(
      `/projects/${projectId}/history/${version}`,
    ),

  // Design Tokens
  getTokens: (id: string) =>
    api.get<ApiResponse<ResolvedTokensResponse>>(`/projects/${id}/tokens`),

  updateTokens: (
    id: string,
    data: { theme?: string; overrides?: Record<string, string> },
  ) =>
    api.put<ApiResponse<ResolvedTokensResponse>>(
      `/projects/${id}/tokens`,
      data,
    ),

  exportTokens: (id: string, format: "json" | "css" = "json") =>
    api.get(`/projects/${id}/tokens/export`, {
      params: { format },
      responseType: format === "css" ? "text" : "json",
    }),
};

// Catalog API (admin only)
export const catalogApi = {
  // Get current published catalog (or latest draft)
  getCurrent: () => api.get<ApiResponse<Catalog>>("/catalog"),

  // Get all catalog versions (admin only)
  getVersions: (params?: {
    status?: string;
    limit?: number;
    offset?: number;
  }) =>
    api.get<ApiResponseWithPagination<Catalog[]>>("/catalog/versions", {
      params,
    }),

  // Get specific version (admin only)
  getVersion: (version: number) =>
    api.get<ApiResponse<Catalog>>(`/catalog/${version}`),

  // Create new catalog version (admin only)
  create: (data: { catalog?: CatalogContent; copyFromVersion?: number }) =>
    api.post<ApiResponse<Catalog>>("/catalog", data),

  // Update draft catalog (admin only)
  update: (version: number, data: { catalog: CatalogContent }) =>
    api.patch<ApiResponse<Catalog>>(`/catalog/${version}`, data),

  // Publish catalog version (admin only)
  publish: (version: number, data?: { notes?: string }) =>
    api.post<ApiResponse<Catalog>>(`/catalog/${version}/publish`, data),

  // Delete catalog version (admin only, draft only)
  delete: (version: number) =>
    api.delete<ApiResponse<{ message: string }>>(`/catalog/${version}`),

  // Import catalog from JSON (admin only)
  import: (data: { catalog: CatalogContent; publishImmediately?: boolean }) =>
    api.post<ApiResponse<Catalog>>("/catalog/import", data),

  // Get audit log for a catalog version (admin only)
  getAuditLog: (version: number) =>
    api.get<ApiResponse<AuditLogEntry[]>>(`/catalog/${version}/audit-log`),

  // Generate preview token for draft catalog (editor+ only)
  createPreviewToken: (version: number) =>
    api.post<
      ApiResponse<{
        token: string;
        previewUrl: string;
        version: number;
        expiresAt: string;
        expiresInSeconds: number;
      }>
    >(`/catalog/${version}/preview-token`),
};

// Types
export interface User {
  id: string;
  email: string;
  name: string;
  role: "admin" | "editor" | "viewer";
  active: boolean;
  lastLoginAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateUserData {
  email: string;
  password: string;
  name: string;
  role: "admin" | "editor" | "viewer";
}

export interface UpdateUserData {
  name?: string;
  role?: "admin" | "editor" | "viewer";
  active?: boolean;
}

export interface Client {
  id: string;
  code: string;
  name: string;
  description?: string;
  contact?: {
    referent?: string;
    email?: string;
    phone?: string;
    address?: string;
  };
  productCatalog?: object;
  notes?: string;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateClientData {
  code: string;
  name: string;
  description?: string;
  contact?: Client["contact"];
  notes?: string;
}

export interface UpdateClientData {
  name?: string;
  description?: string;
  contact?: Client["contact"];
  notes?: string;
  active?: boolean;
  productCatalog?: object;
}

export interface Project {
  id: string;
  name: string;
  clientId: string | { id: string; code: string; name: string };
  config: object;
  productCatalogVersion: number;
  status: "draft" | "valid" | "exported";
  lastExportedAt?: string;
  validation?: {
    valid: boolean;
    errors: ValidationError[];
    checkedAt: string;
  };
  deployedUrl?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateProjectData {
  name: string;
  clientId: string;
  config?: object;
}

export interface UpdateProjectData {
  name?: string;
  config?: object;
  changeDescription?: string;
  productCatalogVersion?: number;
  deployedUrl?: string;
}

export interface ValidationError {
  path: string;
  code: string;
  message: string;
}

export interface HistoryEntry {
  id: string;
  projectId: string;
  version: number;
  config?: object;
  action: "created" | "updated" | "exported" | "restored";
  changeDescription?: string;
  savedAt: string;
  savedBy: { id: string; name: string; email: string };
}

export interface Pagination {
  total: number;
  limit: number;
  offset: number;
}

export interface ApiResponseWithPagination<T> extends ApiResponse<T> {
  pagination: Pagination;
}

// Catalog types

// Variante sezione con status opzionale
export interface SectionVariant {
  id: string;
  label: string;
  status?: "planned" | "deprecated";
}

export interface CatalogSection {
  allowedSlots: string[];
  variants: SectionVariant[] | string[];
  label?: string;
  description?: string;
  // props?: Record<string, unknown>;
  // variantProps?: Record<string, Record<string, unknown>>;
  props?: Record<string, FieldSchema>;
  variantProps?: Record<string, Record<string, FieldSchema>>;
}

// Slot con metadati
export interface CatalogSlot {
  label: string;
  description: string;
  position: string;
  allowsMultiple: boolean;
  width?: string;
  responsive?: {
    mobile: string;
    tablet: string;
    desktop: string;
  };
  mobileOptions?: string[];
}

// Layout variant per template
export interface LayoutVariant {
  id: string;
  label: string;
  hasSidebar: boolean;
  sidebarPosition?: "left" | "right";
}

// Layout template
export interface TemplateLayout {
  hasSidebar: boolean;
  sidebarPosition?: "left" | "right";
  sidebarSticky?: boolean;
  sidebarMobile?: "drawer" | "below" | "hidden" | "tabs";
  fullWidth?: boolean;
}

export interface CatalogTemplate {
  slots: string[];
  pageKind?: string;
  label?: string;
  description?: string;
  recommended?: string[];
  layout?: TemplateLayout;
  layoutVariants?: LayoutVariant[];
}

// Formato nuovo: oggetto con id
export interface CatalogTheme {
  id: string;
  label: string;
  description?: string;
  preview?: { primary: string; secondary: string };
  tokens?: Record<string, string>;
}

// Design Token definition
export interface TokenDefinition {
  type: "color" | "fontFamily" | "size" | "number" | "shadow";
  category:
    | "colors"
    | "typography"
    | "spacing"
    | "borders"
    | "shadows"
    | "layout";
  label: string;
  description?: string;
  editable: "client" | "auto" | "advanced" | "fixed";
  derivedFrom?: string;
  transform?: string;
  generates?: string[];
  options?: Array<{ value: string; label: string; style?: string }>;
  control?: {
    type: "slider" | "preset";
    min?: number;
    max?: number;
    step?: number;
    unit?: string;
    options?: Array<{ value: string; label: string }>;
  };
}

// Font pairing
export interface FontPairing {
  id: string;
  heading: string;
  body: string;
  label: string;
}

// Design Tokens container
export interface DesignTokens {
  schema?: string;
  version?: number;
  defaults: Record<string, string>;
  definitions: Record<string, TokenDefinition>;
  fontPairings?: FontPairing[];
}

// Editable token per l'editor UI
export interface EditableToken {
  key: string;
  definition: TokenDefinition;
  value: string;
  origin: "default" | "theme" | "override";
  defaultValue: string;
  themeValue?: string;
}

// Risposta API resolved tokens
export interface ResolvedTokensResponse {
  themeId: string;
  overrides: Record<string, string>;
  resolved: Record<string, string>;
  origins: Record<string, string>;
  editableTokens: EditableToken[];
  fontPairings?: FontPairing[];
}

// Formato nuovo: oggetto con id
export interface CatalogAdapter {
  id: string;
  label: string;
  description?: string;
  capabilities?: string[];
  baseUrl?: string;
}

export interface CatalogRestrictions {
  catalogVersion?: number;
  allowedThemes?: string[];
  allowedPageKinds?: string[];
  allowedTemplates?: string[];
  allowedSections?: string[];
  allowedAdapters?: string[];
  customSections?: Record<string, CatalogSection>;
  customAdapters?: string[];
}

export interface MergedCatalog {
  version: number;
  sourceVersion: number;
  clientRestricted: boolean;
  themes: CatalogTheme[];
  slots?: Record<string, CatalogSlot>;
  allowedPageKinds: string[];
  templates: Record<string, CatalogTemplate>;
  sections: Record<string, CatalogSection>;
  adapters: CatalogAdapter[];
  bindingTypes?: Record<string, unknown>;
  designTokens?: DesignTokens;
}

export interface CatalogContent {
  allowedPageKinds: string[];
  slots?: Record<string, CatalogSlot>;
  templates: Record<string, CatalogTemplate>;
  sections: Record<string, CatalogSection>;
  themes?: CatalogTheme[];
  adapters?: CatalogAdapter[];
  bindingTypes?: Record<string, unknown>;
  designTokens?: DesignTokens;
}

export interface Catalog {
  id: string;
  version: number;
  status: "draft" | "published";
  catalog: CatalogContent;
  publishedAt?: string;
  createdAt: string;
  updatedAt: string;
  createdBy?: { id: string; name: string; email: string };
  updatedBy?: { id: string; name: string; email: string };
}

// Settings API
export const settingsApi = {
  /** Public settings (any authenticated user) */
  getPublic: () =>
    api.get<ApiResponse<Record<string, unknown>>>("/settings/public"),

  /** All settings with definitions (admin only) */
  getAll: () => api.get<ApiResponse<SettingsData>>("/settings"),

  update: (settings: { key: string; value: unknown }[]) =>
    api.patch<ApiResponse<{ updated: string[]; message: string }>>(
      "/settings",
      { settings },
    ),

  reset: (keys: string[]) =>
    api.post<ApiResponse<{ reset: string[]; message: string }>>(
      "/settings/reset",
      { keys },
    ),
};

// Settings types
export interface SettingConstraints {
  min?: number;
  max?: number;
  options?: { value: string; label: string }[];
  maxLength?: number;
}

export interface SettingDependency {
  key: string;
  value: unknown;
}

export interface SettingDefinitionDto {
  key: string;
  label: string;
  description: string;
  category: string;
  type:
    | "text"
    | "number"
    | "select"
    | "checkbox"
    | "duration"
    | "textarea"
    | "password";
  defaultValue: unknown;
  currentValue: unknown;
  isModified: boolean;
  advanced: boolean;
  constraints?: SettingConstraints;
  dependsOn?: SettingDependency;
}

export interface SettingCategoryDto {
  id: string;
  label: string;
  icon: string;
  order: number;
}

export interface SettingsData {
  definitions: SettingDefinitionDto[];
  categories: SettingCategoryDto[];
}

export interface AuditLogEntry {
  _id: string;
  userId: {
    _id: string;
    name: string;
    email: string;
  } | null;
  action: string;
  resourceType: string;
  resourceId: string;
  details?: Record<string, unknown>;
  ip?: string;
  createdAt: string;
}
